import { Card } from "../../components/ui/Card";
import { RecentConversations } from "../../components/admin/RecentConversations";
import {
  Database,
  WholeWord,
  TextSearch,
  Loader2,
  CheckCircle,
  XCircle,
  Percent,
  Clock,
  Edit,
  Archive,
  Plus,
  AlertCircle,
  ExternalLink,
  Users,
  CreditCard,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@clerk/clerk-react";
import { createApiClient } from "../../lib/api/client";

interface SearchResult {
  id: string;
  query: string;
  search_type: "SEMANTIC" | "KEYWORDS";
  total_results: number;
  results: Array<{ id: string; score: number }>;
  execution_time_ms: number;
  is_successful: boolean;
}

interface SearchResponse {
  results: SearchResult[];
  total: number;
  page: number;
  size: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

interface SearchCounts {
  semantic: number;
  keywords: number;
  successful: number;
  failed: number;
}

interface DocumentCounts {
  total: {
    active_documents: number;
    vectorstore_records: number;
  };
}

interface DocumentActivity {
  id: string;
  title: string;
  action: "created" | "updated" | "archived";
  timestamp: string;
}

interface DocumentActivityResponse {
  activities: DocumentActivity[];
  total: number;
}

interface SubscriptionCounts {
  active: number;
  stripe_paid: number;
  admin_granted: number;
}

interface UnansweredMessage {
  id: string;
  content: string;
  created_at: string;
  conversation_id: string;
  user_id: string | null;
}

interface UnansweredMessagesResponse {
  messages: UnansweredMessage[];
  total: number;
}

function useAnalyticsApi() {
  const { getToken } = useAuth();
  const api = createApiClient(() => getToken());

  return {
    async listSearches(page: number = 0, size: number = 10) {
      const response = await api.get<SearchResponse>("/analytics/searches", {
        params: { page, size },
      });
      return response.data;
    },
    async getTotalDocuments() {
      const response = await api.get<DocumentCounts>(
        "/analytics/stats/documents/total"
      );
      return response.data;
    },
    async getSearchCounts() {
      const response = await api.get<SearchCounts>(
        "/analytics/stats/searches/count"
      );
      return response.data;
    },
    async getRecentDocumentActivity() {
      const response = await api.get<DocumentActivityResponse>(
        "/analytics/documents/activity"
      );
      return response.data;
    },
    async getSubscriptionCounts() {
      const response = await api.get<SubscriptionCounts>(
        "/analytics/stats/subscriptions/count"
      );
      return response.data;
    },
    async getUnansweredMessages(limit: number = 10) {
      const response = await api.get<UnansweredMessagesResponse>(
        "/analytics/messages/unanswered",
        { params: { limit } }
      );
      return response.data;
    },
  };
}

export function DashboardPage() {
  const navigate = useNavigate();
  const analyticsApi = useAnalyticsApi();
  const {
    data: searchData,
    isLoading: isSearchesLoading,
    isError: isSearchesError,
  } = useQuery({
    queryKey: ["searches"],
    queryFn: () => analyticsApi.listSearches(),
    refetchOnMount: "always",
  });

  const {
    data: unansweredData,
    isLoading: isUnansweredLoading,
    isError: isUnansweredError,
  } = useQuery({
    queryKey: ["unanswered-messages"],
    queryFn: () => analyticsApi.getUnansweredMessages(10),
    refetchOnMount: "always",
  });

  const {
    data: documentsData,
    isLoading: isDocumentsLoading,
    isError: isDocumentsError,
  } = useQuery({
    queryKey: ["documents", "total"],
    queryFn: () => analyticsApi.getTotalDocuments(),
    refetchOnMount: "always",
  });

  const {
    data: searchCountsData,
    isLoading: isSearchCountsLoading,
    isError: isSearchCountsError,
  } = useQuery({
    queryKey: ["searches", "counts"],
    queryFn: () => analyticsApi.getSearchCounts(),
    refetchOnMount: "always",
  });

  const {
    data: subscriptionData,
    isLoading: isSubscriptionsLoading,
    isError: isSubscriptionsError,
  } = useQuery({
    queryKey: ["subscriptions", "counts"],
    queryFn: () => analyticsApi.getSubscriptionCounts(),
    refetchOnMount: "always",
  });

  const {
    data: activityData,
    isLoading: isActivityLoading,
    isError: isActivityError,
  } = useQuery({
    queryKey: ["documents", "activity"],
    queryFn: () => analyticsApi.getRecentDocumentActivity(),
    refetchOnMount: "always",
  });

  const getActionIcon = (action: string) => {
    switch (action) {
      case "created":
        return <Plus className="h-4 w-4 text-brand-teal" />;
      case "updated":
        return <Edit className="h-4 w-4 text-brand-primary" />;
      case "archived":
        return <Archive className="h-4 w-4 text-brand-error" />;
      default:
        return null;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    // Create a Date object from the timestamp
    // If timestamp doesn't end with 'Z', assume it's UTC and add 'Z'
    const dateString = timestamp.endsWith("Z") ? timestamp : timestamp + "Z";
    const date = new Date(dateString);

    // Convert to EST/EDT timezone
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    };

    const formatter = new Intl.DateTimeFormat("en-CA", options);
    const formattedParts = formatter.formatToParts(date);

    // Reconstruct in YYYY-MM-DD HH:MM:SS format
    const datePart = `${formattedParts.find((p) => p.type === "year")?.value}-${
      formattedParts.find((p) => p.type === "month")?.value
    }-${formattedParts.find((p) => p.type === "day")?.value}`;
    const timePart = `${formattedParts.find((p) => p.type === "hour")?.value}:${
      formattedParts.find((p) => p.type === "minute")?.value
    }:${formattedParts.find((p) => p.type === "second")?.value}`;

    return `${datePart} ${timePart}`;
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-brand-text">Dashboard Overview</h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-brand-primary/10 p-3">
              <Database className="h-6 w-6 text-brand-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">Documents</p>
              {isDocumentsLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
              ) : isDocumentsError ? (
                "Error"
              ) : (
                <div className="space-y-1">
                  <p className="text-2xl font-thin text-brand-text">
                    {documentsData?.total.active_documents || 0}
                  </p>
                  {/* {documentsData?.total.active_documents !==
                    documentsData?.total.vectorstore_records && (
                    <p className="text-xs text-brand-muted">
                      {documentsData?.total.vectorstore_records || 0} indexés (
                      {documentsData?.total.active_documents
                        ? (
                            (documentsData.total.vectorstore_records /
                              documentsData.total.active_documents) *
                            100
                          ).toFixed(2)
                        : "0.00"}
                      %)
                    </p>
                  )} */}
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-purple-100 p-3">
              <TextSearch className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Recherches sémantiques
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.semantic || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-brand-warning/10 p-3">
              <WholeWord className="h-6 w-6 text-brand-warning" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Recherches par mots-clés
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.keywords || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-brand-teal/10 p-3">
              <CheckCircle className="h-6 w-6 text-brand-teal" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Recherches réussies
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.successful || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-brand-error/10 p-3">
              <XCircle className="h-6 w-6 text-brand-error" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Recherches échouées
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData?.failed || 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-brand-primary/10 p-3">
              <Percent className="h-6 w-6 text-brand-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Taux de réussite
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSearchCountsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSearchCountsError ? (
                  "Error"
                ) : (
                  searchCountsData &&
                  (searchCountsData.successful + searchCountsData.failed > 0
                    ? `${Math.round(
                        (searchCountsData.successful /
                          (searchCountsData.successful +
                            searchCountsData.failed)) *
                          100
                      )}%`
                    : "N/A")
                )}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-teal-100 p-3">
              <Users className="h-6 w-6 text-teal-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Abonnements actifs
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSubscriptionsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSubscriptionsError ? (
                  "Error"
                ) : (
                  subscriptionData?.active ?? 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-emerald-100 p-3">
              <CreditCard className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Abonnements payés
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSubscriptionsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSubscriptionsError ? (
                  "Error"
                ) : (
                  subscriptionData?.stripe_paid ?? 0
                )}
              </p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-brand-warning/10 p-3">
              <ShieldCheck className="h-6 w-6 text-brand-warning" />
            </div>
            <div>
              <p className="text-sm font-medium text-brand-muted">
                Assignés par admin
              </p>
              <p className="text-2xl font-thin text-brand-text">
                {isSubscriptionsLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-brand-muted" />
                ) : isSubscriptionsError ? (
                  "Error"
                ) : (
                  subscriptionData?.admin_granted ?? 0
                )}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Conversations */}
      <RecentConversations limit={5} />

      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-brand-muted" />
          <h3 className="text-lg font-semibold text-brand-text">
            Recent Document Activity
          </h3>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-brand-border">
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Action
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    When
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {isActivityLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-muted" />
                    </td>
                  </tr>
                ) : isActivityError ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      Failed to load recent activity
                    </td>
                  </tr>
                ) : activityData?.activities.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      No recent activity
                    </td>
                  </tr>
                ) : (
                  activityData?.activities.map((activity) => (
                    <tr key={activity.id}>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <div className="flex items-center gap-1.5">
                          {getActionIcon(activity.action)}
                          <span className="capitalize">{activity.action}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{activity.title}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-brand-muted">
                        {formatTimestamp(activity.timestamp)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Unanswered Questions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-brand-warning" />
            <h3 className="text-lg font-semibold text-brand-text">
              Questions sans réponse
            </h3>
            {unansweredData && unansweredData.total > 0 && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-brand-warning/10 text-brand-warning">
                {unansweredData.total} total
              </span>
            )}
          </div>
          <button
            onClick={() => navigate("/admin/conversations?filter=unanswered")}
            className="text-sm text-brand-primary hover:opacity-80 flex items-center gap-1"
          >
            Voir toutes
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-brand-border">
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Question
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {isUnansweredLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-muted" />
                    </td>
                  </tr>
                ) : isUnansweredError ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      Failed to load unanswered questions
                    </td>
                  </tr>
                ) : unansweredData?.messages.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      <div className="flex flex-col items-center py-4">
                        <CheckCircle className="h-8 w-8 text-brand-teal mb-2" />
                        <span>Aucune question sans réponse</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  unansweredData?.messages.map((message) => (
                    <tr key={message.id} className="hover:bg-brand-surface">
                      <td className="px-4 py-3 text-sm">
                        <div className="max-w-md truncate" title={message.content}>
                          {message.content}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-brand-muted">
                        {formatTimestamp(message.created_at)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm">
                        <button
                          onClick={() => navigate(`/admin/conversations/${message.conversation_id}`)}
                          className="text-brand-primary hover:opacity-80 flex items-center gap-1"
                        >
                          Voir
                          <ExternalLink className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Recent Searches */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <TextSearch className="h-5 w-5 text-brand-muted" />
          <h3 className="text-lg font-semibold text-brand-text">
            Recherches récentes
          </h3>
        </div>

        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-brand-border">
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Query
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Results
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-brand-muted">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-border">
                {isSearchesLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      <Loader2 className="mx-auto h-6 w-6 animate-spin text-brand-muted" />
                    </td>
                  </tr>
                ) : isSearchesError ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      Failed to load recent searches
                    </td>
                  </tr>
                ) : searchData?.results.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-4 text-center text-sm text-brand-muted"
                    >
                      No recent searches
                    </td>
                  </tr>
                ) : (
                  searchData?.results.map((search) => {
                    const highestScore = search.results.reduce(
                      (max, result) => Math.max(max, result.score),
                      0
                    );

                    return (
                      <tr key={search.id}>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              search.search_type === "SEMANTIC"
                                ? "bg-purple-100 text-purple-600"
                                : "bg-brand-warning/10 text-brand-warning"
                            }`}
                          >
                            {search.search_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-brand-text">
                          {search.query}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-brand-muted">
                          {search.total_results} results •{" "}
                          {search.execution_time_ms}ms
                          {search.is_successful && highestScore > 0 && (
                            <span className="ml-2 rounded-full bg-brand-primary/10 px-2 py-1 text-xs font-medium text-brand-primary">
                              Score: {highestScore.toFixed(2)}
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-medium ${
                              search.is_successful
                                ? "bg-brand-teal/10 text-brand-teal"
                                : "bg-brand-error/10 text-brand-error"
                            }`}
                          >
                            {search.is_successful ? "Success" : "Failed"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
